import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ShareNetwork } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AccessMatrix } from "@/components/traffic/AccessMatrix";
import { MissedCreditCard } from "@/components/traffic/MissedCreditCard";
import { NoBuyerCard } from "@/components/traffic/NoBuyerCard";
import { TryLeadCard } from "@/components/traffic/TryLeadCard";
import { reachesNoBuyer } from "@/components/traffic/CoverageLine";

const SHOW = [["all", "Everyone"], ["media_buyer", "Media buyers"], ["affiliate", "Affiliates"]];

export default function TrafficAccess() {
  const { user } = useAuth();
  const isAdmin = user?.org_type === "internal" && ["super_admin", "admin"].includes(user?.role);
  if (!isAdmin) return <div className="p-8 text-sm" data-testid="traffic-access-denied">Only admins can manage traffic access.</div>;
  return <TrafficAccessBody />;
}

function TrafficAccessBody() {
  const [data, setData] = useState(null);
  const [show, setShow] = useState("all");
  const [q, setQ] = useState("");
  const [busyKey, setBusyKey] = useState(null);

  const load = () => api.get("/admin/traffic-access").then((r) => setData(r.data))
    .catch((e) => { toast.error(e.response?.data?.detail || "Couldn't load traffic access"); setData(false); });
  useEffect(() => { load(); }, []);

  const patch = (partner, state) => setData((d) => ({
    ...d, partners: d.partners.map((m) => (m.id === partner.id ? { ...m, all_pages: state.all_pages, page_ids: state.page_ids } : m)),
  }));
  const toggle = async (partner, page, on) => {
    setBusyKey(`${partner.id}:${page.id}`);
    try {
      const { data: state } = await api.put(`/admin/traffic-access/${partner.kind}/${partner.id}/pages/${page.id}`, { on });
      patch(partner, state);
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save");
      return false;
    } finally { setBusyKey(null); }
  };
  const give = async (m) => {
    const partner = data.partners.find((p) => p.id === m.media_buyer_id);
    if (partner && await toggle(partner, { id: m.give_page_id }, true)) {
      toast.success(`${m.media_buyer} can now send traffic to ${m.give_title}`);
      setData((d) => ({ ...d, missed: d.missed.filter((x) => !(x.media_buyer_id === m.media_buyer_id && x.give_page_id === m.give_page_id)) }));
    }
  };

  const partners = useMemo(() => (data ? data.partners.filter((m) => show === "all" || m.kind === show) : []), [data, show]);
  const pages = useMemo(() => {
    const n = q.trim().toLowerCase();
    return !data ? [] : data.pages.filter((p) => !n || `${p.title} ${p.slug} ${p.firm_name || ""}`.toLowerCase().includes(n));
  }, [data, q]);
  const unconnected = data ? data.pages.filter((p) => p.kind !== "shared" && p.status === "published" && !p.is_test
    && !data.partners.some((m) => m.all_pages || m.page_ids.includes(p.id))).length : 0;
  const deadEnds = data ? data.pages.filter((p) => reachesNoBuyer(p.coverage)
    && data.partners.some((m) => (m.all_pages && p.status === "published") || m.page_ids.includes(p.id))).length : 0;

  return (
    <div className="p-6 lg:p-8" data-testid="traffic-access-page">
      <div className="label-mono">Media buyers &amp; affiliates</div>
      <h1 className="font-chivo font-black text-4xl sm:text-5xl uppercase tracking-tighter leading-none mb-2 flex items-center gap-3">
        <ShareNetwork size={36} weight="bold" /> Traffic Access
      </h1>
      <p className="text-sm text-zinc-700 max-w-3xl mb-5">
        Tick who may send traffic to each page. Media buyers only see and get credit on pages ticked for them. Affiliate traffic to a page that isn&apos;t ticked is turned away.
        Turn on <b>All pages</b> for someone to give them every live page, including new ones. Everyone else gets new pages only when you tick them.
      </p>
      {data === null && <div className="label-mono">Loading…</div>}
      {data === false && <div className="text-sm text-red-700">Couldn&apos;t load. Refresh to try again.</div>}
      {data && (
        <>
          <MissedCreditCard missed={data.missed} onGive={give} busyKey={busyKey} />
          <NoBuyerCard />
          <div className={`mb-4 border-2 px-3 py-2 text-xs font-bold ${deadEnds ? "border-red-700 bg-red-50 text-red-900" : "border-emerald-700 bg-emerald-50 text-emerald-900"}`}
               data-testid="traffic-dead-ends">
            {deadEnds
              ? `${deadEnds} page${deadEnds === 1 ? "" : "s"} with traffic access reach no buyer right now — see the red rows below.`
              : "Every page someone can send traffic to reaches at least one live buyer."}
          </div>
          <TryLeadCard pages={data.pages} partners={data.partners} />
          <div className="flex items-center gap-3 flex-wrap mb-3">
            <div className="inline-flex border-2 border-black" role="group">
              {SHOW.map(([k, label]) => (
                <button key={k} type="button" onClick={() => setShow(k)} data-testid={`traffic-show-${k}`}
                        className={`px-3 py-1.5 text-[10px] uppercase tracking-widest transition-colors duration-100 ${show === k ? "bg-black text-white" : "bg-white hover:bg-zinc-100"}`}>{label}</button>
              ))}
            </div>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a page or buyer" data-testid="traffic-search"
                   className="border-2 border-black px-3 py-1.5 text-sm w-full sm:w-64" />
            <span className={`label-mono ${unconnected ? "text-amber-800 font-bold" : "text-emerald-800"}`} data-testid="traffic-unconnected">
              {unconnected ? `${unconnected} live buyer page${unconnected === 1 ? "" : "s"} no one can send traffic to` : "Every live buyer page has someone who can send traffic"}
            </span>
          </div>
          {partners.length === 0
            ? <div className="text-sm text-zinc-600 border-2 border-dashed border-zinc-400 p-6 text-center" data-testid="traffic-no-partners">No media buyers or affiliates yet.</div>
            : <AccessMatrix pages={pages} partners={partners} onToggle={toggle} onAllPages={(m, d) => { patch(m, d); load(); }} busyKey={busyKey} />}
        </>
      )}
    </div>
  );
}
